import { FC, forwardRef, useEffect, useRef, useState } from 'react';
import { Box, DropdownList, Input } from '@base-app';
import { useOnClickOutside } from '@common/hooks';
import { DropdownIconStyled, DropdownIconRotatedStyled, FlexBoxStyled, IconContainerStyled } from './Select.styles';
import { SelectProps } from './Select.types';
import { inputWidth } from '@common/utils';
import { DropdownListItem } from '../DropdownList/DropdownList.types';

const Select: FC<SelectProps> = forwardRef<HTMLInputElement, SelectProps>((props: SelectProps, ref) => {
  const {
    disabled,
    error,
    errorDescription,
    fullWidth,
    id,
    items,
    label,
    name,
    onChange,
    placeholder,
    value,
    width,
    secondaryLabel,
    testId,
    itemTestId,
  } = props;
  const containerRef = useRef(null);
  const [focusItem, setFocusItem] = useState<number | undefined>(undefined);
  const [isComponentVisible, setIsComponentVisible] = useState<boolean>(false);
  const [inputItem, setInputItem] = useState<DropdownListItem>();
  const componentWidth: string = inputWidth(width, fullWidth);

  useEffect(() => {
    const item = items.find((item) => item.id === value);
    setInputItem(item);
  }, [value]);

  const handleListVisibility = (): void => {
    setIsComponentVisible((prevState: boolean): boolean => !prevState);
  };

  const handleClickOutsideMenu = (): void => {
    setIsComponentVisible(false);
    setFocusItem(undefined);
  };

  useOnClickOutside(containerRef, handleClickOutsideMenu);

  const handleItemClick = (item: DropdownListItem): void => {
    setInputItem(item);
    onChange?.(item.id);
    setIsComponentVisible(false);
    setFocusItem(undefined);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    event.preventDefault();
    if (event.key === 'ArrowDown' && !isComponentVisible) {
      handleListVisibility();
    }
    if (event.key === 'ArrowDown' && isComponentVisible) {
      setFocusItem((prevState: number | undefined): number | undefined => {
        if (prevState === undefined) {
          return 0;
        }
        if (prevState === items.length - 1) {
          return 0;
        }
        return prevState + 1;
      });
    }
    if (event.key === 'ArrowUp' && isComponentVisible) {
      setFocusItem((prevState: number | undefined): number | undefined => {
        if (prevState === undefined) {
          return items.length - 1;
        }
        if (prevState === 0) {
          return items.length - 1;
        }
        return prevState - 1;
      });
    }
    if (event.key === 'Enter' && focusItem !== undefined && isComponentVisible) {
      handleItemClick(items[focusItem]);
    }
  };

  return (
    <Box
      css={{
        position: 'relative',
        width: componentWidth,
      }}
      ref={containerRef}
    >
      <FlexBoxStyled
        role="combobox"
        aria-expanded={isComponentVisible}
        onClick={handleListVisibility}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      >
        <Input
          testId={testId}
          ref={ref}
          value={inputItem?.label}
          error={error}
          errorDescription={errorDescription}
          fullWidth={true}
          id={id}
          label={label}
          name={name}
          onClick={handleListVisibility}
          autoComplete="off"
          placeholder={placeholder}
          readOnly={true}
          type="text"
          secondaryLabel={secondaryLabel}
        />
        <IconContainerStyled data-testid="dropdownBtn">
          {isComponentVisible ? <DropdownIconRotatedStyled /> : <DropdownIconStyled />}
        </IconContainerStyled>
      </FlexBoxStyled>
      {isComponentVisible && (
        <DropdownList
          testId={itemTestId}
          css={{
            mt: error != null && error && '-$7',
            width,
          }}
          focusItem={focusItem}
          items={items}
          onSelect={(item) => handleItemClick(item)}
          width={componentWidth}
          disabled={disabled}
          onMouseMove={() => setFocusItem(undefined)}
        />
      )}
    </Box>
  );
});

Select.displayName = 'Select';

export default Select;
